import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { computeBreakeven, statusLine } from './breakeven';
import { calculate } from './calc';
import { computeCashflow } from './cashflow';
import { applyScenario, SCENARIO_LABELS } from './scenarios';
import {
  costCategoriesFor,
  type CostCategory,
  type EventData,
  type EventMeta,
} from './types';

// Light-theme palette tuned for printing / email attachments. The PPTX
// deck stays dark; the PDF report deliberately leans paper-document.
const C = {
  text: '#111827',
  textDim: '#4B5563',
  textFaint: '#9CA3AF',
  accent: '#0EA5E9',
  accentDark: '#075985',
  border: '#E5E7EB',
  borderStrong: '#D1D5DB',
  stripe: '#F3F4F6',
  cardBg: '#F9FAFB',
  success: '#15803D',
  danger: '#B91C1C',
  warn: '#B45309',
  bannerBg: '#0F1B2C',
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 14;
const MARGIN_TOP = 16;
const MARGIN_BOTTOM = 18;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;

// Explicit four-sided margin for autoTable. Without `top`/`bottom`,
// autoTable's auto-pagination can't position continuation pages correctly,
// which is why long cashflow tables previously seemed to be missing rows.
const TABLE_MARGIN = {
  top: MARGIN_TOP + 4,
  bottom: MARGIN_BOTTOM,
  left: MARGIN_X,
  right: MARGIN_X,
};

type Totals = ReturnType<typeof calculate>;

// ---------- public --------------------------------------------------------

export async function exportEventToPdf(
  meta: EventMeta,
  data: EventData,
): Promise<void> {
  const isMeetup = meta.event_type === 'meetup';
  const effective = isMeetup ? data : applyScenario(data);
  const totals = calculate(effective);
  const gross = data.vatMode === 'gross' ? 1 + data.vatRate : 1;
  const currencyCode = meta.currency;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  doc.setFont('helvetica', 'normal');

  drawCoverPage(doc, meta, data, totals, gross, currencyCode, isMeetup, effective);
  drawIncomesPage(doc, meta, data, gross, currencyCode);
  drawCostsPage(doc, meta, data, totals, gross, currencyCode);
  if (!isMeetup) {
    drawBreakevenPage(doc, meta, effective, gross, currencyCode);
  }
  if (!isMeetup && meta.start_date) {
    drawCashflowPage(doc, meta, effective, meta.start_date, gross, currencyCode);
  }
  drawPageFooters(doc, meta);

  doc.save(makeFileName(meta));
}

// ---------- shared helpers ------------------------------------------------

function hex(h: string): [number, number, number] {
  const s = h.replace('#', '');
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

function setText(doc: jsPDF, color: string) {
  doc.setTextColor(...hex(color));
}
function setFill(doc: jsPDF, color: string) {
  doc.setFillColor(...hex(color));
}
function setDraw(doc: jsPDF, color: string) {
  doc.setDrawColor(...hex(color));
}

// jsPDF's built-in helvetica is WinAnsi-encoded — characters like "ł", "č",
// or U+2212 minus sign aren't representable and render as garbage. Use the
// ISO currency code (always ASCII) and an ASCII hyphen for the sign.
function fmtMoney(value: number, code: string): string {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded).toLocaleString('en-US').replace(/,/g, ' ');
  return `${sign}${abs} ${code}`;
}

function fmtRange(min: number, max: number, code: string): string {
  if (Math.round(min) === Math.round(max)) return fmtMoney(min, code);
  return `${fmtMoney(min, code)} – ${fmtMoney(max, code)}`;
}

// jsPDF leaks the `charSpace` option into global doc state — set it on one
// text call and every subsequent call keeps the wide spacing. Wrap the
// pattern so the reset is impossible to forget.
function trackedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  spacing: number,
) {
  doc.text(text, x, y, { charSpace: spacing });
  doc.setCharSpace(0);
}

function netColor(value: number): string {
  return value >= 0 ? C.success : C.danger;
}

function pageHeader(doc: jsPDF, meta: EventMeta, kicker: string, title: string): number {
  // Thin accent rule at top of page.
  setDraw(doc, C.accent);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_X, MARGIN_TOP - 4, MARGIN_X + 30, MARGIN_TOP - 4);
  // Kicker
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setText(doc, C.accent);
  doc.text(kicker.toUpperCase(), MARGIN_X, MARGIN_TOP + 1);
  // Section title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  setText(doc, C.text);
  doc.text(title, MARGIN_X, MARGIN_TOP + 10);
  // Subtitle (event name)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setText(doc, C.textDim);
  doc.text(meta.name || 'Untitled', MARGIN_X, MARGIN_TOP + 16);

  return MARGIN_TOP + 24;
}

function sectionDivider(doc: jsPDF, y: number, label?: string): number {
  setDraw(doc, C.border);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  if (label) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setText(doc, C.textDim);
    doc.text(label.toUpperCase(), MARGIN_X, y + 6);
    return y + 11;
  }
  return y + 4;
}

// Compact axis-label format (e.g. "4.5M PLN", "240k PLN") — fmtMoney is
// too long for axis ticks.
function fmtCompact(value: number, code: string): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let body: string;
  if (abs >= 1_000_000) body = (abs / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  else if (abs >= 1_000) body = (abs / 1_000).toFixed(0) + 'k';
  else body = Math.round(abs).toString();
  return `${sign}${body} ${code}`;
}

// Cashflow trajectory as a shape-based bar chart. Drawn directly with
// jsPDF primitives so it stays in vector form and prints cleanly.
function drawCashflowChart(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  weeks: { weekStart: string; cumulative: number }[],
  posColor: string,
  negColor: string,
  currencyCode: string,
) {
  // Panel background
  setFill(doc, '#FFFFFF');
  setDraw(doc, C.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'FD');

  if (weeks.length === 0) return;

  const yLabelW = 18;
  const xLabelH = 5;
  const plotX = x + yLabelW;
  const plotY = y + 3;
  const plotW = w - yLabelW - 3;
  const plotH = h - xLabelH - 5;

  let minVal = 0;
  let maxVal = 0;
  for (const w of weeks) {
    if (w.cumulative < minVal) minVal = w.cumulative;
    if (w.cumulative > maxVal) maxVal = w.cumulative;
  }
  if (minVal === 0 && maxVal === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    setText(doc, C.textFaint);
    doc.text('No movement in this timeline', x + w / 2, y + h / 2, { align: 'center' });
    return;
  }

  const span = (maxVal - minVal) || 1;
  const pad = span * 0.06;
  const yLo = minVal - pad;
  const yHi = maxVal + pad;
  const yRange = yHi - yLo;

  const yToPx = (v: number) => plotY + plotH - ((v - yLo) / yRange) * plotH;
  const zeroY = yToPx(0);

  // Zero baseline
  setDraw(doc, C.borderStrong);
  doc.setLineWidth(0.4);
  doc.line(plotX, zeroY, plotX + plotW, zeroY);

  // Y-axis labels at min, 0 (if in range), max
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  setText(doc, C.textDim);
  if (maxVal > 0) {
    doc.text(fmtCompact(maxVal, currencyCode), plotX - 1, yToPx(maxVal) + 1.5, {
      align: 'right',
    });
  }
  if (minVal < 0) {
    doc.text(fmtCompact(minVal, currencyCode), plotX - 1, yToPx(minVal) - 0.5, {
      align: 'right',
    });
  }
  if (maxVal > 0 && minVal < 0) {
    doc.text('0', plotX - 1, zeroY + 1.2, { align: 'right' });
  }

  // Bars
  const barSlot = plotW / weeks.length;
  const barW = Math.max(0.6, barSlot * 0.72);
  const barOffset = (barSlot - barW) / 2;

  weeks.forEach((week, i) => {
    const bx = plotX + i * barSlot + barOffset;
    const yBar = yToPx(week.cumulative);
    if (week.cumulative >= 0) {
      const bh = zeroY - yBar;
      if (bh > 0.1) {
        setFill(doc, posColor);
        doc.rect(bx, yBar, barW, bh, 'F');
      }
    } else {
      const bh = yBar - zeroY;
      if (bh > 0.1) {
        setFill(doc, negColor);
        doc.rect(bx, zeroY, barW, bh, 'F');
      }
    }
  });

  // X-axis labels — sample to ~7 marks so dates don't crowd.
  const maxLabels = 7;
  const step = Math.max(1, Math.ceil(weeks.length / maxLabels));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  setText(doc, C.textDim);
  weeks.forEach((week, i) => {
    const isLast = i === weeks.length - 1;
    if (i % step !== 0 && !isLast) return;
    const bx = plotX + i * barSlot + barOffset + barW / 2;
    // Strip year so labels stay compact (MM-DD).
    const label = week.weekStart.slice(5);
    doc.text(label, bx, y + h - 1.5, { align: 'center' });
  });
}

// Truncate text to fit a max width, appending "…" if it overflows.
function fitWidth(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (doc.getTextWidth(text.slice(0, mid) + '…') <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

// ---------- page 1: cover -------------------------------------------------

function drawCoverPage(
  doc: jsPDF,
  meta: EventMeta,
  data: EventData,
  totals: Totals,
  gross: number,
  currencyCode: string,
  isMeetup: boolean,
  effective: EventData,
) {
  // Dark banner across the top — the only "branded" touch on the document.
  setFill(doc, C.bannerBg);
  doc.rect(0, 0, PAGE_W, 75, 'F');
  setFill(doc, C.accent);
  doc.rect(0, 75, PAGE_W, 1.5, 'F');

  // Banner content
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setText(doc, C.accent);
  trackedText(doc, 'REVENUE ESTIMATE', MARGIN_X, 20, 0.6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(255, 255, 255);
  const name = meta.name || 'Untitled';
  doc.text(fitWidth(doc, name, CONTENT_W), MARGIN_X, 38);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  setText(doc, '#CBD5E1');
  const dateLine = [meta.start_date, meta.end_date].filter(Boolean).join(' – ');
  const locLine = [meta.city, meta.country_code].filter(Boolean).join(', ');
  const subBits = [isMeetup ? 'Meetup' : 'Conference', dateLine, locLine].filter(Boolean);
  doc.text(subBits.join('   ·   '), MARGIN_X, 50);

  // Meta strip at bottom of banner
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setText(doc, '#94A3B8');
  const vatLine =
    data.vatMode === 'gross'
      ? `Gross (+${(data.vatRate * 100).toFixed(1)}% VAT)`
      : 'Net of VAT';
  const scen = isMeetup ? null : `Scenario: ${SCENARIO_LABELS[data.scenario.active]}`;
  const footerBits = [meta.currency, vatLine, scen].filter(Boolean) as string[];
  doc.text(footerBits.join('   ·   '), MARGIN_X, 68);
  doc.text('estimate.events', PAGE_W - MARGIN_X, 68, { align: 'right' });

  // ------ Body of cover: executive summary ------
  let y = 92;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setText(doc, C.accent);
  trackedText(doc, 'EXECUTIVE SUMMARY', MARGIN_X, y, 0.6);
  y += 5;

  // Headline NET number
  const netMin = totals.net.min * gross;
  const netMax = totals.net.max * gross;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setText(doc, C.textDim);
  doc.text('Net (revenue minus costs)', MARGIN_X, y + 4);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  setText(doc, netColor((netMin + netMax) / 2));
  doc.text(fmtRange(netMin, netMax, currencyCode), MARGIN_X, y + 16);
  y += 22;

  // Break-even status (conference only)
  if (!isMeetup) {
    const be = computeBreakeven(effective);
    const status = statusLine(be, meta.currency);
    if (status) {
      const toneColor =
        status.tone === 'good' ? C.success :
        status.tone === 'warn' ? C.warn :
        status.tone === 'bad' ? C.danger : C.textDim;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setText(doc, toneColor);
      doc.text(status.text, MARGIN_X, y);
      y += 8;
    }
  }

  // 2 x 2 KPI grid
  y += 4;
  const tileGap = 4;
  const tileW = (CONTENT_W - tileGap) / 2;
  const tileH = 24;

  const tiles: {
    label: string;
    value: string;
    sub?: string;
    color?: string;
  }[] = [
    {
      label: 'Revenue',
      value: fmtRange(totals.revenue.min * gross, totals.revenue.max * gross, currencyCode),
      sub: `${totals.ticketCount.toLocaleString('en-US')} tickets · ${totals.sponsorCount.toLocaleString('en-US')} sponsors`,
      color: C.accent,
    },
    {
      label: 'Costs',
      value: fmtMoney(totals.costs * gross, currencyCode),
      sub: `${totals.addonCount} add-ons enabled`,
      color: C.danger,
    },
    {
      label: 'Per attendee — revenue',
      value:
        totals.ticketCount > 0
          ? fmtMoney(((totals.perAttendee.revenue.min + totals.perAttendee.revenue.max) / 2) * gross, currencyCode)
          : '—',
      sub: 'mid of min/max',
      color: C.accent,
    },
    {
      label: 'Per attendee — cost',
      value:
        totals.ticketCount > 0
          ? fmtMoney(totals.perAttendee.cost * gross, currencyCode)
          : '—',
      sub: 'fixed costs / ticket',
      color: C.text,
    },
  ];

  for (let i = 0; i < tiles.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = MARGIN_X + col * (tileW + tileGap);
    const ty = y + row * (tileH + tileGap);
    setFill(doc, C.cardBg);
    setDraw(doc, C.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, ty, tileW, tileH, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setText(doc, C.textDim);
    trackedText(doc, tiles[i].label.toUpperCase(), x + 4, ty + 5, 0.4);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    setText(doc, tiles[i].color ?? C.text);
    doc.text(fitWidth(doc, tiles[i].value, tileW - 8), x + 4, ty + 14);

    if (tiles[i].sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setText(doc, C.textFaint);
      doc.text(tiles[i].sub!, x + 4, ty + 20);
    }
  }
  y += 2 * (tileH + tileGap);

  // Composition strip — what makes up the revenue
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setText(doc, C.accent);
  trackedText(doc, 'REVENUE COMPOSITION (AT MAX PRICES)', MARGIN_X, y, 0.6);
  y += 5;

  const compTotal =
    totals.ticketRevenue.max * gross +
    totals.sponsorRevenue.max * gross +
    totals.addonRevenue * gross;
  const segs = [
    { label: 'Tickets', value: totals.ticketRevenue.max * gross, color: C.accent },
    { label: 'Sponsors', value: totals.sponsorRevenue.max * gross, color: C.accentDark },
    { label: 'Add-ons', value: totals.addonRevenue * gross, color: '#7C3AED' },
  ];
  const barY = y + 4;
  const barH = 10;
  let barX = MARGIN_X;
  if (compTotal > 0) {
    for (const s of segs) {
      const w = (s.value / compTotal) * CONTENT_W;
      if (w < 0.1) continue;
      setFill(doc, s.color);
      doc.rect(barX, barY, w, barH, 'F');
      barX += w;
    }
  } else {
    setFill(doc, C.border);
    doc.rect(MARGIN_X, barY, CONTENT_W, barH, 'F');
  }

  // Legend
  y = barY + barH + 5;
  let legendX = MARGIN_X;
  for (const s of segs) {
    setFill(doc, s.color);
    doc.rect(legendX, y - 2.5, 3, 3, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setText(doc, C.textDim);
    const pct = compTotal > 0 ? (s.value / compTotal) * 100 : 0;
    const text = `${s.label}  ${fmtMoney(s.value, currencyCode)}  (${pct.toFixed(0)}%)`;
    doc.text(text, legendX + 5, y);
    legendX += doc.getTextWidth(text) + 12;
  }
}

// ---------- page 2: incomes -----------------------------------------------

function drawIncomesPage(
  doc: jsPDF,
  meta: EventMeta,
  data: EventData,
  gross: number,
  currencyCode: string,
) {
  doc.addPage();
  let y = pageHeader(doc, meta, 'Section 1', 'Income breakdown');

  // Tickets
  y = drawTableSection(
    doc,
    y,
    'Tickets',
    [['Name', 'Qty', 'Price (min)', 'Price (max)', 'Subtotal (min)', 'Subtotal (max)']],
    data.tickets.map((t) => [
      t.name,
      t.qty.toLocaleString('en-US'),
      fmtMoney(t.priceMin * gross, currencyCode),
      fmtMoney(t.priceMax * gross, currencyCode),
      fmtMoney(t.qty * t.priceMin * gross, currencyCode),
      fmtMoney(t.qty * t.priceMax * gross, currencyCode),
    ]),
    [1, 2, 3, 4, 5],
    'No tickets configured.',
  );

  // Sponsors
  y = drawTableSection(
    doc,
    y,
    'Sponsors',
    [['Tier', 'Qty', 'Price (min)', 'Price (max)', 'Subtotal (min)', 'Subtotal (max)']],
    data.sponsors.map((s) => [
      s.name,
      s.qty.toLocaleString('en-US'),
      fmtMoney(s.priceMin * gross, currencyCode),
      fmtMoney(s.priceMax * gross, currencyCode),
      fmtMoney(s.qty * s.priceMin * gross, currencyCode),
      fmtMoney(s.qty * s.priceMax * gross, currencyCode),
    ]),
    [1, 2, 3, 4, 5],
    'No sponsor tiers configured.',
  );

  // Add-ons
  const addonRows = data.addons.map((a) => {
    const mult = a.multiplier && a.multiplier > 0 ? a.multiplier : 1;
    const total = a.enabled ? a.price * mult * gross : 0;
    return [
      a.name,
      a.enabled ? 'Yes' : 'No',
      fmtMoney(a.price * gross, currencyCode),
      mult.toString(),
      fmtMoney(total, currencyCode),
    ];
  });
  drawTableSection(
    doc,
    y,
    'Add-ons',
    [['Name', 'Enabled', 'Price', 'Multiplier', 'Total']],
    addonRows,
    [1, 2, 3, 4],
    'No add-ons configured.',
  );
}

// ---------- page 3: costs -------------------------------------------------

function drawCostsPage(
  doc: jsPDF,
  meta: EventMeta,
  data: EventData,
  totals: Totals,
  gross: number,
  currencyCode: string,
) {
  doc.addPage();
  let y = pageHeader(doc, meta, 'Section 2', 'Cost breakdown');

  // Build one combined table grouped by category with subtotal rows.
  const cats = costCategoriesFor(meta.event_type);
  const body: (string | number)[][] = [];
  let grand = 0;
  for (const c of cats) {
    const items = data.costs[c.key as CostCategory] ?? [];
    if (items.length === 0) continue;
    for (const item of items) {
      body.push([
        c.label,
        item.name || '(no name)',
        fmtMoney(item.amount * gross, currencyCode),
      ]);
    }
    const sub = items.reduce((acc, i) => acc + i.amount, 0) * gross;
    grand += sub;
    body.push([
      { content: c.label, styles: { fontStyle: 'italic', textColor: hex(C.textDim) } } as any,
      { content: 'subtotal', styles: { fontStyle: 'italic', textColor: hex(C.textDim) } } as any,
      {
        content: fmtMoney(sub, currencyCode),
        styles: { fontStyle: 'italic', textColor: hex(C.textDim), halign: 'right' },
      } as any,
    ]);
  }

  if (body.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    setText(doc, C.textDim);
    doc.text('No costs recorded yet.', MARGIN_X, y + 6);
    return;
  }

  autoTable(doc, {
    startY: y,
    head: [['Category', 'Item', 'Amount']],
    body,
    foot: [[
      { content: 'TOTAL', styles: { fontStyle: 'bold', textColor: hex(C.text) } } as any,
      '',
      {
        content: fmtMoney(grand, currencyCode),
        styles: { fontStyle: 'bold', textColor: hex(C.text), halign: 'right' },
      } as any,
    ]],
    theme: 'striped',
    margin: TABLE_MARGIN,
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2.4,
      textColor: hex(C.text),
      lineColor: hex(C.border),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: hex(C.accent),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    footStyles: {
      fillColor: hex(C.cardBg),
      textColor: hex(C.text),
    },
    alternateRowStyles: { fillColor: hex(C.stripe) },
    columnStyles: { 2: { halign: 'right' } },
  });

  // Sanity check vs effective totals so reader sees both lines.
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setText(doc, C.textFaint);
  const note =
    Math.round(grand) === Math.round(totals.costs * gross)
      ? `Raw plan total matches projected total.`
      : `Note: projected (scenario-applied) total is ${fmtMoney(totals.costs * gross, currencyCode)}.`;
  doc.text(note, MARGIN_X, finalY + 6);
}

// ---------- page 4: break-even (conference only) --------------------------

function drawBreakevenPage(
  doc: jsPDF,
  meta: EventMeta,
  effective: EventData,
  gross: number,
  currencyCode: string,
) {
  doc.addPage();
  let y = pageHeader(doc, meta, 'Section 3', 'Break-even analysis');

  const be = computeBreakeven(effective);
  const status = statusLine(be, meta.currency);

  // Status callout
  if (status) {
    const toneColor =
      status.tone === 'good' ? C.success :
      status.tone === 'warn' ? C.warn :
      status.tone === 'bad' ? C.danger : C.textDim;
    setFill(doc, C.cardBg);
    setDraw(doc, toneColor);
    doc.setLineWidth(0.6);
    doc.roundedRect(MARGIN_X, y, CONTENT_W, 14, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setText(doc, toneColor);
    doc.text(status.text, MARGIN_X + 5, y + 9);
    y += 18;
  }

  const scenarioBlock = (label: string, s: typeof be.min, startY: number): number => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setText(doc, C.accent);
    trackedText(doc, label.toUpperCase(), MARGIN_X, startY, 0.5);
    let yy = startY + 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    setText(doc, s.profitable ? C.success : C.danger);
    doc.text(s.profitable ? 'Profitable' : 'Below break-even', MARGIN_X, yy);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setText(doc, C.textDim);
    const gapText = s.profitable
      ? `Headroom ${fmtMoney(-s.gap * gross, currencyCode)}`
      : `Gap ${fmtMoney(s.gap * gross, currencyCode)}`;
    doc.text(gapText, MARGIN_X + 50, yy);
    yy += 6;

    // Lever table
    const rows: (string | number)[][] = [];
    if (!s.profitable) {
      if (s.tickets.computable && s.tickets.needed > 0) {
        const pct = Number.isFinite(s.tickets.pctOfPlan)
          ? `${Math.round(s.tickets.pctOfPlan * 100)}% of plan`
          : '—';
        rows.push(['Sell tickets', s.tickets.needed.toLocaleString('en-US'), pct]);
      }
      if (s.sponsors.computable && s.sponsors.needed > 0) {
        const pct = Number.isFinite(s.sponsors.pctOfPlan)
          ? `${Math.round(s.sponsors.pctOfPlan * 100)}% of plan`
          : '—';
        rows.push(['Land sponsors', s.sponsors.needed.toLocaleString('en-US'), pct]);
      }
      rows.push(['Cut costs by', fmtMoney(s.costDown.needed * gross, currencyCode), '']);
    }

    if (rows.length > 0) {
      autoTable(doc, {
        startY: yy,
        head: [['Lever', 'Target', 'Of plan']],
        body: rows,
        theme: 'plain',
        margin: TABLE_MARGIN,
        styles: {
          font: 'helvetica',
          fontSize: 10,
          cellPadding: 2,
          textColor: hex(C.text),
          lineColor: hex(C.border),
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: hex(C.stripe),
          textColor: hex(C.textDim),
          fontStyle: 'bold',
        },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
      });
      yy =
        (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable
          .finalY + 4;
    }
    return yy + 4;
  };

  y = scenarioBlock('At MIN prices', be.min, y);
  scenarioBlock('At MAX prices', be.max, y);
}

// ---------- page 5: cashflow (conference only with start_date) ------------

function drawCashflowPage(
  doc: jsPDF,
  meta: EventMeta,
  effective: EventData,
  startDate: string,
  gross: number,
  currencyCode: string,
) {
  const cf = computeCashflow(effective, startDate);
  if (cf.weeks.length === 0) return;

  doc.addPage();
  let y = pageHeader(doc, meta, 'Section 4', 'Cashflow timeline');

  // Summary cards row (3 wide)
  const gap = 4;
  const cardW = (CONTENT_W - 2 * gap) / 3;
  const cardH = 22;
  const cards: { label: string; value: string; color?: string; sub?: string }[] = [
    {
      label: 'Total inflow',
      value: fmtMoney(cf.totalIn * gross, currencyCode),
      color: C.success,
    },
    {
      label: 'Total outflow',
      value: fmtMoney(cf.totalOut * gross, currencyCode),
      color: C.danger,
    },
    {
      label: 'Final net',
      value: fmtMoney(cf.finalNet * gross, currencyCode),
      color: netColor(cf.finalNet),
    },
  ];
  cards.forEach((c, i) => {
    const x = MARGIN_X + i * (cardW + gap);
    setFill(doc, C.cardBg);
    setDraw(doc, C.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setText(doc, C.textDim);
    trackedText(doc, c.label.toUpperCase(), x + 4, y + 5, 0.4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    setText(doc, c.color ?? C.text);
    doc.text(fitWidth(doc, c.value, cardW - 8), x + 4, y + 15);
  });
  y += cardH + 6;

  // Trough callout — green when positive, red when negative; always shown
  // so the reader can see the worst-case working-capital position at a glance.
  if (cf.trough) {
    const negative = cf.trough.amount < 0;
    const bg = negative ? '#FEF3F2' : '#F0FDF4';
    const accent = negative ? C.danger : C.success;
    setFill(doc, bg);
    setDraw(doc, accent);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN_X, y, CONTENT_W, 14, 1.5, 1.5, 'FD');
    const tDay = cf.trough.daysToEvent;
    const tDayLabel =
      tDay === 0 ? 'event week' : tDay > 0 ? `T-${tDay}d` : `T+${-tDay}d`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setText(doc, accent);
    const label = negative ? 'Cash trough' : 'Lowest balance';
    doc.text(
      `${label}: ${fmtMoney(cf.trough.amount * gross, currencyCode)}   on ${cf.trough.date}  (${tDayLabel})`,
      MARGIN_X + 5,
      y + 9,
    );
    y += 18;
  }

  // Cashflow chart — visualises the full trajectory so the reader doesn't
  // have to scan the whole table to see the shape.
  const chartH = 56;
  drawCashflowChart(
    doc,
    MARGIN_X,
    y,
    CONTENT_W,
    chartH,
    cf.weeks.map((w) => ({
      weekStart: w.weekStart,
      cumulative: w.cumulative * gross,
    })),
    C.success,
    C.danger,
    currencyCode,
  );
  y += chartH + 6;

  // Weekly table — autoTable paginates with the explicit four-sided margin.
  // Continuation pages get a small repeated header via didDrawPage.
  const body = cf.weeks.map((w) => {
    const net = (w.inflow - w.outflow) * gross;
    return [
      w.weekStart,
      `T-${w.daysToEvent}d`,
      fmtMoney(w.inflow * gross, currencyCode),
      fmtMoney(w.outflow * gross, currencyCode),
      {
        content: fmtMoney(net, currencyCode),
        styles: { textColor: hex(netColor(net)) },
      } as never,
      {
        content: fmtMoney(w.cumulative * gross, currencyCode),
        styles: {
          textColor: hex(netColor(w.cumulative)),
          fontStyle: 'bold' as const,
        },
      } as never,
    ];
  });

  let tablePageIdx = 0;
  autoTable(doc, {
    startY: y,
    head: [['Week starting', 'To event', 'Inflow', 'Outflow', 'Net (week)', 'Cumulative']],
    body,
    theme: 'striped',
    margin: TABLE_MARGIN,
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2,
      textColor: hex(C.text),
      lineColor: hex(C.border),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: hex(C.accent),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: { fillColor: hex(C.stripe) },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 20, halign: 'right' },
      2: { halign: 'right', textColor: hex(C.success) },
      3: { halign: 'right', textColor: hex(C.danger) },
      4: { halign: 'right' },
      5: { halign: 'right' },
    },
    didDrawPage: () => {
      tablePageIdx += 1;
      if (tablePageIdx > 1) {
        // Compact continuation header on follow-on pages.
        setDraw(doc, C.accent);
        doc.setLineWidth(0.6);
        doc.line(MARGIN_X, MARGIN_TOP - 4, MARGIN_X + 24, MARGIN_TOP - 4);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        setText(doc, C.accent);
        doc.text('CASHFLOW TIMELINE (CONTINUED)', MARGIN_X, MARGIN_TOP + 1);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        setText(doc, C.textDim);
        doc.text(meta.name || 'Untitled', MARGIN_X, MARGIN_TOP + 7);
      }
    },
  });
}

// ---------- generic table section helper ----------------------------------

function drawTableSection(
  doc: jsPDF,
  startY: number,
  title: string,
  head: string[][],
  body: (string | number)[][],
  rightAlignColumnIndices: number[],
  emptyMessage: string,
): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setText(doc, C.text);
  doc.text(title, MARGIN_X, startY);
  let y = startY + 3;

  if (body.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setText(doc, C.textFaint);
    doc.text(emptyMessage, MARGIN_X, y + 4);
    return y + 12;
  }

  const columnStyles: Record<number, { halign: 'right' }> = {};
  for (const i of rightAlignColumnIndices) columnStyles[i] = { halign: 'right' };

  autoTable(doc, {
    startY: y,
    head,
    body,
    theme: 'striped',
    margin: TABLE_MARGIN,
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2.2,
      textColor: hex(C.text),
      lineColor: hex(C.border),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: hex(C.accent),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: hex(C.stripe) },
    columnStyles,
  });
  return (
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable
      .finalY + 8
  );
}

// ---------- page footers --------------------------------------------------

function drawPageFooters(doc: jsPDF, meta: EventMeta) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    setText(doc, C.textFaint);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const footerY = PAGE_H - 8;
    if (i > 1) {
      // Page 1 has its own banner; skip the muted footer there.
      doc.text(meta.name || 'Untitled', MARGIN_X, footerY);
    }
    doc.text(`${i} / ${total}`, PAGE_W - MARGIN_X, footerY, { align: 'right' });
  }
}

// ---------- filename ------------------------------------------------------

function makeFileName(meta: EventMeta): string {
  const safe = (meta.name || 'event')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const date = meta.start_date ? `_${meta.start_date}` : '';
  return `${safe || 'event'}${date}.pdf`;
}
